import React, { useMemo, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { styled } from "@mui/system";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import RoomIcon from "@mui/icons-material/Room";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import PhoneIcon from "@mui/icons-material/Phone";
import SparkleIcon from "@mui/icons-material/AutoAwesome";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import axios from "axios";
import { API_URL } from "./config";
import { SectionHeading } from "./utils";

const Card = styled(Paper)(({ theme }) => ({
    borderRadius: 28,
    background:
        theme.palette.mode === "light"
            ? "linear-gradient(165deg, rgba(255,255,255,0.93), rgba(255,255,255,0.75))"
            : theme.palette.background.default,
    border: `1px solid ${
        theme.palette.mode === "light" ? "rgba(99,102,241,0.22)" : theme.palette.divider
    }`,
    boxShadow:
        theme.palette.mode === "light"
            ? "0 28px 55px rgba(17,24,39,0.11)"
            : "0 18px 36px rgba(0,0,0,0.55)",
    backdropFilter: "blur(18px)",
}));

const ContactUsSection = () => {
        const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [copied, setCopied] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: "",
        // honeypot:
        website: "",
    });

    const [touched, setTouched] = useState({
        name: false,
        email: false,
        message: false,
    });

    const emailRe = useMemo(
        () =>
            // simple but effective email pattern
            /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i,
        []
    );

    const errors = useMemo(() => {
        const e = {};
        if (!formData.name.trim()) e.name = "Please enter your name.";
        else if (formData.name.trim().length < 2) e.name = "Name looks too short.";

        if (!formData.email.trim()) e.email = "Please enter your email.";
        else if (!emailRe.test(formData.email.trim()))
            e.email = "Email format looks invalid.";

        if (!formData.message.trim()) e.message = "Please write a short message.";
        else if (formData.message.trim().length < 10)
            e.message = "Give us a bit more detail (10+ characters).";

        // honeypot check not surfaced to user
        return e;
    }, [formData, emailRe]);

    const hasErrors = Object.keys(errors).length > 0;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((s) => ({ ...s, [name]: value }));
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched((t) => ({ ...t, [name]: true }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSuccessMessage("");
        setErrorMessage("");

        // basic front-end validation
        setTouched({ name: true, email: true, message: true });
        if (hasErrors) return;

        // honeypot: if filled, silently abort
        if (formData.website) return;

        setSubmitting(true);
        try {
            const { data } = await axios.post(
                `${API_URL}contact_handler.php`,
                {
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    message: formData.message.trim(),
                },
                { withCredentials: true }
            );

            if (data?.success) {
                setSuccessMessage("Message sent! We’ll get back to you shortly.");
                setFormData({ name: "", email: "", message: "", website: "" });
                setTouched({ name: false, email: false, message: false });
            } else {
                setErrorMessage(
                    data?.message || "Failed to send. Please try again in a moment."
                );
            }
        } catch {
            setErrorMessage("Server error. Please try again later.");
        } finally {
            setSubmitting(false);
        }
    };

    const emailAddr = "info@qr-flex.com";
    const phone = "+254 708 142 190";
    const whatsappUrl = `https://wa.me/254708142190?text=${encodeURIComponent(
        "Hi! I’m interested in QR-Flex. Could we talk?"
    )}`;

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            /* ignore */
        }
    };

    return (
        <Box
            component="section"
            sx={{
                py: { xs: 6, md: 10 },
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Container maxWidth="lg">
                <Stack spacing={1.25} sx={{ textAlign: "center", mb: { xs: 3.5, sm: 5 } }}>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Chip
                            icon={<SparkleIcon sx={{ color: "#6366f1 !important" }} />}
                            label="Premium support"
                            sx={{
                                fontWeight: 700,
                                px: 0.5,
                                borderRadius: 99,
                                color: "#4338ca",
                                border: "1px solid rgba(99,102,241,0.25)",
                                background: "rgba(255,255,255,0.75)",
                                backdropFilter: "blur(10px)",
                            }}
                        />
                    </Box>
                    <SectionHeading>Contact Us</SectionHeading>
                    <Typography color="text.secondary" sx={{ fontSize: { xs: "0.98rem", md: "1.1rem" } }}>
                        Have questions about dynamic QR, campaigns, or integrations? We’re here.
                    </Typography>
                </Stack>

                <Grid
                    container
                    rowSpacing={{ xs: 2, sm: 3 }}
                    columnSpacing={{ xs: 2, sm: 3 }}
                    alignItems="stretch"
                    sx={{
                        p: { xs: 0, md: 0 },
                    }}
                >
                    {/* Form */}
                    <Grid item xs={12} md={7}>
                        <Card elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                            <form
                                onSubmit={handleFormSubmit}
                                noValidate
                                style={{ display: "grid", gap: 12 }}
                            >
                                {/* Honeypot (hidden from humans) */}
                                <input
                                    type="text"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleChange}
                                    style={{ display: "none" }}
                                    tabIndex={-1}
                                    autoComplete="off"
                                />

                                <TextField
                                    name="name"
                                    label="Your Name"
                                    fullWidth
                                    required
                                    value={formData.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    name="email"
                                    label="Email"
                                    type="email"
                                    fullWidth
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.email && Boolean(errors.email)}
                                    helperText={touched.email && errors.email}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    name="message"
                                    label="Message"
                                    fullWidth
                                    required
                                    multiline
                                    minRows={4}
                                    value={formData.message}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.message && Boolean(errors.message)}
                                    helperText={touched.message && errors.message}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ alignSelf: "flex-start", pt: 1 }}>
                                                <ChatBubbleOutlineIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                    alignItems={{ xs: "stretch", sm: "center" }}
                                    justifyContent="space-between"
                                    sx={{ mt: 1 }}
                                >
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={submitting}
                                        endIcon={<ArrowOutwardIcon />}
                                        sx={{
                                            fontWeight: 800,
                                            borderRadius: 999,
                                            px: 3.2,
                                            py: 1.25,
                                            background:
                                                "linear-gradient(95deg, #4f46e5 0%, #7c3aed 55%, #0ea5e9 100%)",
                                            boxShadow: "0 12px 30px rgba(79,70,229,0.35)",
                                        }}
                                    >
                                        {submitting ? "Sending…" : "Send Message"}
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        color="success"
                                        size="large"
                                        startIcon={<WhatsAppIcon />}
                                        href={whatsappUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        sx={{
                                            fontWeight: 800,
                                            borderRadius: 999,
                                            px: 3,
                                            py: 1.2,
                                            borderWidth: 1.5,
                                            background: "rgba(255,255,255,0.7)",
                                        }}
                                    >
                                        Chat on WhatsApp
                                    </Button>
                                </Stack>
                            </form>
                        </Card>
                    </Grid>

                    {/* Info / Sidebar */}
                    <Grid item xs={12} md={5}>
                        <Stack spacing={2} sx={{ height: "100%" }}>
                            <Card elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                                <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 1.2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(99,102,241,0.16)", color: "#4f46e5" }}>
                                        <SparkleIcon />
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                        Get in Touch
                                    </Typography>
                                </Stack>
                                <Typography color="text.secondary" sx={{ mb: 2 }}>
                                    Faster replies within business hours.
                                </Typography>

                                <Stack spacing={1.25}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <EmailIcon color="action" />
                                        <Link href={`mailto:${emailAddr}`} underline="hover">
                                            {emailAddr}
                                        </Link>
                                        <IconButton
                                            size="small"
                                            aria-label="Copy email"
                                            onClick={() => copy(emailAddr)}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>

                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <PhoneIcon color="action" />
                                        <Link href={`tel:${phone.replace(/\s+/g, "")}`} underline="hover">
                                            {phone}
                                        </Link>
                                        <IconButton
                                            size="small"
                                            aria-label="Copy phone"
                                            onClick={() => copy(phone)}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>

                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <QueryBuilderIcon color="action" />
                                        <Typography color="text.secondary">
                                            Mon–Fri, 8:00–18:00 (EAT)
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <RoomIcon color="action" />
                                        <Typography color="text.secondary">Nairobi, Kenya</Typography>
                                    </Stack>
                                </Stack>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="body2" color="text.secondary">
                                    Prefer a quick chat? Use WhatsApp for faster coordination, or email if you need to attach files.
                                </Typography>
                            </Card>

                            {/* Subtle CTA strip */}
                            <Card
                                elevation={0}
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    border: (t) =>
                                        `1px solid ${
                                            t.palette.mode === "light"
                                                ? "rgba(99,102,241,0.25)"
                                                : t.palette.divider
                                        }`,
                                    borderRadius: 5,
                                    background:
                                        "linear-gradient(110deg, rgba(255,255,255,0.8), rgba(238,242,255,0.72))",
                                    backdropFilter: "blur(15px)",
                                }}
                            >
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                    justifyContent="space-between"
                                >
                                    <Typography sx={{ fontWeight: 700 }}>
                                        Need to attach documents or a brief?
                                    </Typography>
                                    <Button
                                        href="mailto:info@qr-flex.com?subject=Project%20Brief%20for%20QR-Flex"
                                        variant="text"
                                        sx={{ fontWeight: 800, borderRadius: 999, px: 3 }}
                                    >
                                        Email Project Brief
                                    </Button>
                                </Stack>
                            </Card>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Feedback Alerts */}
                {(successMessage || errorMessage || copied) && (
                    <Box sx={{ mt: 3 }}>
                        {successMessage && (
                            <Alert
                                severity="success"
                                sx={{ mb: 1.5, borderRadius: 2 }}
                                onClose={() => setSuccessMessage("")}
                            >
                                {successMessage}
                            </Alert>
                        )}
                        {errorMessage && (
                            <Alert
                                severity="error"
                                sx={{ mb: 1.5, borderRadius: 2 }}
                                onClose={() => setErrorMessage("")}
                            >
                                {errorMessage}
                            </Alert>
                        )}
                        {copied && (
                            <Alert
                                severity="info"
                                sx={{ borderRadius: 2 }}
                                onClose={() => setCopied(false)}
                            >
                                Copied to clipboard.
                            </Alert>
                        )}
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default ContactUsSection;
